import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useUser } from '../hooks/useUser';
import {
    deleteUserByRole,
    fetchUserByRole,
    resetSubordinateLoginPassword,
    updateUserByRole
} from '../services/users';
import {
    listPatients,
    fetchPatient,
    createPatientAdmin,
    updatePatientAdmin,
    deletePatientAdmin
} from '../services/patients';
import {
    listClinicalNotes,
    fetchClinicalNote,
    createClinicalNote
} from '../services/clinicalNotes';
import { getCallableUsers } from '../services/call';
import { createMeasurement, listMeasurements } from '../services/measurements';
import { createAppointmentAdmin, listAppointments, updateAppointmentAdmin } from '../services/appointments';
import { listLocations } from '../services/locations';
import { listDevices } from '../services/devices';
import { listPatientRecords } from '../services/records';
import DashboardTabs from '../components/dashboard/DashboardTabs';
import { dashboardTabs } from '../components/dashboard/dashboardMockData';
import CaregiverGeneralSection from '../components/caregiver/CaregiverGeneralSection';
import CaregiverPersonalDataSection from '../components/caregiver/CaregiverPersonalDataSection';
import CaregiverAssessmentsSection from '../components/caregiver/CaregiverAssessmentsSection';
import CaregiverMonitoringSection from '../components/caregiver/CaregiverMonitoringSection';
import CaregiverWearableSection from '../components/caregiver/CaregiverWearableSection';
import CaregiverDocumentsSection from '../components/documents/CaregiverDocumentsSection';
import { passwordPolicyMessage, validatePasswordPolicy } from '../utils/passwordPolicy';

dayjs.locale('pt');

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const dialogContentSx = {
    px: 3,
    pb: 1,
    pt: 0.5
};

const dialogActionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1
};

const secondaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px'
    }
};

const quickTabSx = {
    minWidth: 140,
    minHeight: 46,
    px: 2.2,
    justifyContent: 'center',
    borderRadius: '14px',
    borderColor: '#D9D9D9',
    borderWidth: '1px',
    color: '#3D3D3D',
    textTransform: 'none',
    fontWeight: 550,
    fontSize: '0.92rem',
    bgcolor: '#FFFFFF',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '1px'
    }
};

const quickTabActiveSx = {
    ...quickTabSx,
    bgcolor: '#D63731',
    color: '#fff',
    borderColor: '#D63731',
    '&:hover': {
        bgcolor: '#D63731',
        borderColor: '#D63731'
    }
};

const createEmptyReferenceContact = () => ({
    name: '',
    kinship: '',
    age: '',
    phone: ''
});

const createEmptyEquipmentItem = () => ({
    type: 'Tablet',
    details: ''
});

const normalizeEquipmentItems = (extra = {}) => {
    const arrayItems = Array.isArray(extra.equipment_items)
        ? extra.equipment_items
              .map((item) => ({
                  type: String(item?.type || '').trim() || 'Tablet',
                  details: String(item?.details || item?.notes || item?.identifier || '').trim()
              }))
              .filter((item) => item.type || item.details)
        : [];

    if (arrayItems.length > 0) {
        return arrayItems;
    }

    if (extra.has_tablet_equipment || extra.tablet_serial_number) {
        return [
            {
                type: 'Tablet',
                details: extra.tablet_serial_number || ''
            }
        ];
    }

    return [createEmptyEquipmentItem()];
};

const normalizeReferenceContacts = (extra = {}) => {
    const arrayContacts = Array.isArray(extra.reference_contacts)
        ? extra.reference_contacts
              .map((item) => ({
                  name: item?.name || '',
                  kinship: item?.kinship || '',
                  age: item?.age || '',
                  phone: item?.phone || ''
              }))
              .filter((item) => item.name || item.kinship || item.age || item.phone)
        : [];

    if (arrayContacts.length > 0) {
        return arrayContacts;
    }

    const legacyContacts = [
        {
            name: extra.household_member_name || '',
            kinship: extra.household_member_kinship || '',
            age: extra.household_member_age || '',
            phone: extra.household_member_phone || extra.household_member_occupation || ''
        },
        {
            name: extra.reference_person_name || '',
            kinship: extra.reference_person_kinship || '',
            age: extra.reference_person_age || '',
            phone: extra.reference_person_phone || extra.reference_person_occupation || ''
        },
        {
            name: extra.alt_contact_name || '',
            kinship: extra.alt_contact_kinship || '',
            age: extra.alt_contact_age || '',
            phone: extra.alt_contact_phone || ''
        }
    ].filter((item) => item.name || item.kinship || item.age || item.phone);

    return legacyContacts.length > 0 ? legacyContacts : [createEmptyReferenceContact()];
};

const toApiDate = (date) => {
    if (!date) {
        return undefined;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
    }
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date);
    if (!match) {
        return undefined;
    }
    return `${match[3]}-${match[2]}-${match[1]}`;
};

const toUiDate = (date) => {
    if (!date) {
        return '';
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [y, m, d] = date.split('-');
        return `${d}/${m}/${y}`;
    }

    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
        return date;
    }

    return parsed.toLocaleDateString('pt-PT');
};

const toDateInputValue = (date) => {
    if (!date) {
        return '';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
    }
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date);
    if (!match) {
        return '';
    }
    return `${match[3]}-${match[2]}-${match[1]}`;
};

const calculateAgeFromDate = (date) => {
    const normalized = toDateInputValue(date);
    if (!normalized) {
        return '';
    }

    const birthDate = new Date(`${normalized}T00:00:00`);
    if (Number.isNaN(birthDate.getTime())) {
        return '';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
    }

    return age >= 0 ? String(age) : '';
};

const toApiGender = (value) => {
    const normalized = (value || '').trim().toUpperCase();
    if (!normalized) return undefined;

    const map = {
        WOMAN: 'WOMAN',
        FEMALE: 'WOMAN',
        FEMININO: 'WOMAN',
        MULHER: 'WOMAN',
        MAN: 'MAN',
        MALE: 'MAN',
        MASCULINO: 'MAN',
        HOMEM: 'MAN',
        TRANS_WOMAN: 'TRANS_WOMAN',
        TRANS_MAN: 'TRANS_MAN',
        NONBINARY: 'NONBINARY',
        NAO_BINARIO: 'NONBINARY',
        AGENDER: 'AGENDER',
        GENDERQUEER: 'GENDERQUEER',
        PREFER_NO_ANSWER: 'PREFER_NO_ANSWER',
        NOT_LISTED: 'NOT_LISTED',
        UNKNOWN: 'UNKNOWN'
    };

    return map[normalized];
};

const CaregiverDashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { roleUuid } = useParams();
    const { user } = useUser();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [caregiver, setCaregiver] = useState(null);
    const [readOnly, setReadOnly] = useState(true);
    const [personalForm, setPersonalForm] = useState({
        roleUuid: roleUuid || '',
        name: '',
        email: '',
        gender: '',
        phone: '',
        age: '',
        birthDate: '',
        street: '',
        zip: '',
        parish: '',
        municipality: '',
        locationUuid: '',
        idCard: '',
        nif: '',
        healthNumber: '',
        niss: '',
        maritalStatus: '',
        education: '',
        professionalStatus: '',
        kinshipToDependent: '',
        livesInIsolation: '',
        referenceContacts: [createEmptyReferenceContact()],
        careDuration: '',
        hasCareSupport: '',
        supportType: '',
        psychosocialNotes: '',
        equipmentItems: [{ type: 'Tablet', details: '' }],
        accountCreated: '',
        activeSince: '',
        isSubordinate: false,
        subordinateUsername: ''
    });
    const [snapshot, setSnapshot] = useState(null);
    const [patient, setPatient] = useState(null);
    const [patientReadOnly, setPatientReadOnly] = useState(true);
    const [patientSubmitting, setPatientSubmitting] = useState(false);
    const [showPatientForm, setShowPatientForm] = useState(false);
    const [caregiverCallableUser, setCaregiverCallableUser] = useState(null);
    const [patientForm, setPatientForm] = useState({
        uuid: '',
        name: '',
        gender: '',
        age: '',
        birthDate: '',
        street: '',
        zip: '',
        parish: '',
        municipality: '',
        idCard: '',
        nif: '',
        healthNumber: '',
        niss: '',
        maritalStatus: '',
        education: '',
        professionalStatus: '',
        disability: ''
    });
    const [patientSnapshot, setPatientSnapshot] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [appointmentsPageIndex, setAppointmentsPageIndex] = useState(0);
    const [appointmentsPageTokens, setAppointmentsPageTokens] = useState({ 0: null });
    const [appointmentsTotalCount, setAppointmentsTotalCount] = useState(0);
    const [hasNextAppointmentsPage, setHasNextAppointmentsPage] = useState(false);
    const [notesReloadKey, setNotesReloadKey] = useState(0);
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [notesError, setNotesError] = useState('');
    const [appointmentNotes, setAppointmentNotes] = useState([]);
    const [creatingNote, setCreatingNote] = useState(false);
    const [createNoteError, setCreateNoteError] = useState('');
    const [latestMeasurementSummary, setLatestMeasurementSummary] = useState(null);
    const [loadingMeasurements, setLoadingMeasurements] = useState(false);
    const [moodMeasurements, setMoodMeasurements] = useState({ caregiver: [], patient: [] });
    const [psychologicalStressValue, setPsychologicalStressValue] = useState('');
    const [psychologicalStressTs, setPsychologicalStressTs] = useState('');
    const [loadingPsychologicalStress, setLoadingPsychologicalStress] = useState(false);
    const [savingPsychologicalStress, setSavingPsychologicalStress] = useState(false);
    const [upcomingAppointments, setUpcomingAppointments] = useState([]);
    const [loadingUpcomingAppointments, setLoadingUpcomingAppointments] = useState(false);
    const [appointmentReloadKey, setAppointmentReloadKey] = useState(0);
    const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
    const [appointmentDialogSubmitting, setAppointmentDialogSubmitting] = useState(false);
    const [appointmentDialogError, setAppointmentDialogError] = useState('');
    const [appointmentForm, setAppointmentForm] = useState({
        clinician: '',
        date: dayjs().startOf('day'),
        time: '10:00',
        duration: 45
    });
    const [teamMembers, setTeamMembers] = useState([]);
    const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
    const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);
    const [rescheduleError, setRescheduleError] = useState('');
    const [selectedUpcomingAppointment, setSelectedUpcomingAppointment] = useState(null);
    const [cancelUpcomingAppointmentDialogOpen, setCancelUpcomingAppointmentDialogOpen] = useState(false);
    const [cancelUpcomingAppointmentSubmitting, setCancelUpcomingAppointmentSubmitting] = useState(false);
    const [cancelUpcomingAppointmentError, setCancelUpcomingAppointmentError] = useState('');
    const [rescheduleForm, setRescheduleForm] = useState({
        date: dayjs().startOf('day'),
        time: '10:00',
        duration: 45
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [subordinatePasswordDialogOpen, setSubordinatePasswordDialogOpen] = useState(false);
    const [subordinatePasswordForm, setSubordinatePasswordForm] = useState({
        password: '',
        confirmPassword: ''
    });
    const [subordinatePasswordSubmitting, setSubordinatePasswordSubmitting] = useState(false);
    const [subordinatePasswordError, setSubordinatePasswordError] = useState('');
    const [showSubordinatePassword, setShowSubordinatePassword] = useState(false);
    const [showSubordinatePasswordConfirm, setShowSubordinatePasswordConfirm] = useState(false);
    const [locationOptions, setLocationOptions] = useState([]);
    const [wearableDevice, setWearableDevice] = useState(null);
    const [loadingWearableDevice, setLoadingWearableDevice] = useState(false);
    const [wearableRecords, setWearableRecords] = useState([]);
    const [loadingWearableRecords, setLoadingWearableRecords] = useState(false);
    const measurementSubjectUuid = patient?.uuid || '';

    const activeTab = (searchParams.get('tab') || 'geral').toLowerCase();
    const initialMonitoringMetric = (searchParams.get('metric') || '').toUpperCase();
    const activeRole = useMemo(
        () => (user?.roles || []).find((role) => role.uuid === user?.selectedRole) || null,
        [user?.roles, user?.selectedRole]
    );
    const canCreatePrivateNotes = activeRole?.clinician_type === 'PSYCHOLOGY';
    const handleMainTabClick = (tab) => {
        const normalizedTab = String(tab || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();

        if (normalizedTab === 'inicio') {
            navigate('/');
            return;
        }

        if (normalizedTab === 'agendamentos') {
            navigate('/appointments');
            return;
        }

        if (normalizedTab === 'equipa tecnica') {
            navigate('/doctors');
            return;
        }

        if (normalizedTab === 'cuidadores e pacientes') {
            navigate('/caregivers');
            return;
        }

        if (normalizedTab === 'documentos') {
            navigate('/documents');
        }
    };

    useEffect(() => {
        const loadCaregiver = async () => {
            if (!user.selectedRole || !roleUuid) {
                return;
            }

            setLoading(true);
            setError('');
            try {
                const response = await fetchUserByRole({
                    userRoleUuid: user.selectedRole,
                    roleUuid
                });
                const data = response?.data || null;
                setCaregiver(data);

                const extra = data?.user_data || {};
                const nextForm = {
                    roleUuid: data?.role_uuid || roleUuid,
                    name: data?.name || '',
                    email: data?.email || '',
                    gender: data?.gender || data?.sex || '',
                    phone: data?.phone || '',
                    age: calculateAgeFromDate(data?.birth_date || '') || extra.age || '',
                    birthDate: toDateInputValue(data?.birth_date || ''),
                    street: data?.address?.street ?? extra.street ?? '',
                    zip: data?.address?.zip ?? extra.zip ?? '',
                    parish: extra.parish || '',
                    municipality: extra.municipality || '',
                    locationUuid: data?.location?.uuid || '',
                    idCard: extra.id_card || '',
                    nif: extra.nif || '',
                    healthNumber: extra.health_number || '',
                    niss: extra.niss || extra.social_security || '',
                    maritalStatus: extra.marital_status || '',
                    education: extra.education || '',
                    professionalStatus: extra.professional_status || '',
                    kinshipToDependent: extra.kinship_to_dependent || '',
                    livesInIsolation: extra.lives_in_isolation || '',
                    referenceContacts: normalizeReferenceContacts(extra),
                    careDuration: extra.care_duration || '',
                    hasCareSupport: extra.has_care_support || '',
                    supportType: extra.support_type || '',
                    psychosocialNotes: extra.psychosocial_notes || '',
                    equipmentItems: normalizeEquipmentItems(extra),
                    accountCreated: toUiDate(data?.created || ''),
                    activeSince: toDateInputValue(extra.active_since || ''),
                    isSubordinate: Boolean(data?.is_subordinate),
                    subordinateUsername: data?.username || ''
                };
                setPersonalForm(nextForm);
                setSnapshot(nextForm);
            } catch (err) {
                setError(
                    err?.response?.data?.description ||
                        'Falha ao carregar dados completos do cuidador.'
                );
            } finally {
                setLoading(false);
            }
        };

        void loadCaregiver();
    }, [roleUuid, user.selectedRole]);

    useEffect(() => {
        const loadLocations = async () => {
            if (!user.selectedRole) {
                return;
            }

            try {
                const response = await listLocations({
                    userRoleUuid: user.selectedRole,
                    paginationLimit: 50
                });
                const list = Array.isArray(response?.data) ? response.data : [];
                setLocationOptions(list);
            } catch {
                setLocationOptions([]);
            }
        };

        void loadLocations();
    }, [user.selectedRole]);

    useEffect(() => {
        const loadWearableDevice = async () => {
            if (!user.selectedRole || !roleUuid) {
                setWearableDevice(null);
                return;
            }

            setLoadingWearableDevice(true);
            try {
                const response = await listDevices({
                    userRoleUuid: user.selectedRole,
                    caregiverRoleUuid: roleUuid,
                    deviceTypes: ['WEARABLE'],
                    paginationLimit: 20
                });

                const list = Array.isArray(response?.data) ? response.data : [];
                setWearableDevice(list[0] || null);
            } catch {
                setWearableDevice(null);
            } finally {
                setLoadingWearableDevice(false);
            }
        };

        void loadWearableDevice();
    }, [roleUuid, user.selectedRole]);

    useEffect(() => {
        const loadLinkedPatient = async () => {
            if (!user.selectedRole || !roleUuid) {
                return;
            }

            try {
                const listResp = await listPatients({
                    userRoleUuid: user.selectedRole,
                    caregiverRoleUuid: roleUuid
                });
                const list = Array.isArray(listResp?.data) ? listResp.data : [];
                const found = list.length > 0 ? list[0] : null;

                if (!found) {
                    setPatient(null);
                    setShowPatientForm(false);
                    return;
                }

                const fetchResp = await fetchPatient({
                    userRoleUuid: user.selectedRole,
                    patientUuid: found.uuid
                });
                const fullPatient = fetchResp?.data || found;
                const extra = fullPatient.user_data || {};
                const nextPatientForm = {
                    uuid: fullPatient.uuid || found.uuid || '',
                    name: fullPatient.name || found.name || '',
                    gender: fullPatient.gender || fullPatient.sex || found.gender || found.sex || '',
                    age: calculateAgeFromDate(fullPatient.birth_date || '') || extra.age || '',
                    birthDate: toDateInputValue(fullPatient.birth_date || ''),
                    street: fullPatient.address?.street ?? extra.street ?? '',
                    zip: fullPatient.address?.zip ?? extra.zip ?? '',
                    parish: extra.parish || '',
                    municipality: extra.municipality || '',
                    idCard: extra.id_card || '',
                    nif: extra.nif || '',
                    healthNumber: extra.health_number || '',
                    niss: extra.niss || extra.social_security || '',
                    maritalStatus: extra.marital_status || '',
                    education: extra.education || '',
                    professionalStatus: extra.professional_status || '',
                    disability: extra.disability || ''
                };

                setPatient(fullPatient);
                setPatientForm(nextPatientForm);
                setPatientSnapshot(nextPatientForm);
                setPatientReadOnly(true);
                setShowPatientForm(true);
            } catch (err) {
                setPatient(null);
                setShowPatientForm(false);
            }
        };

        void loadLinkedPatient();
    }, [roleUuid, user.selectedRole]);

    useEffect(() => {
        let cancelled = false;
        let intervalId = null;

        const loadCallableCaregiver = async () => {
            if (!user.selectedRole || !roleUuid) {
                return;
            }

            try {
                const response = await getCallableUsers({
                    currentUserId: user.selectedRole,
                    userRoleUuid: user.selectedRole,
                    roleFilters: ['PATIENT_CARE_GIVER']
                });

                if (cancelled) {
                    return;
                }

                const match =
                    (response?.users || []).find((item) => item.userId === roleUuid) || null;
                setCaregiverCallableUser(match);
            } catch {
                if (!cancelled) {
                    setCaregiverCallableUser(null);
                }
            }
        };

        void loadCallableCaregiver();
        intervalId = setInterval(() => {
            void loadCallableCaregiver();
        }, 30000);

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [roleUuid, user.selectedRole]);

    useEffect(() => {
        let cancelled = false;

        const loadTeamMembers = async () => {
            if (!user.selectedRole) {
                return;
            }

            try {
                const currentUserId = user.selectedRole;
                const response = await getCallableUsers({
                    currentUserId,
                    userRoleUuid: user.selectedRole,
                    roleFilters: ['ACCOUNT_ADMIN', 'CLINIC_CARE_GIVER'],
                    paginationLimit: 50,
                    includeCurrentUser: true
                });

                if (!cancelled) {
                    const users = Array.isArray(response?.users) ? response.users : [];
                    setTeamMembers(users);
                    setAppointmentForm((prev) => ({
                        ...prev,
                        clinician: prev.clinician || users[0]?.userId || ''
                    }));
                }
            } catch {
                if (!cancelled) {
                    setTeamMembers([]);
                }
            }
        };

        void loadTeamMembers();

        return () => {
            cancelled = true;
        };
    }, [user.selectedRole]);

    useEffect(() => {
        const loadUpcomingAppointments = async () => {
            if (!user.selectedRole || !roleUuid) {
                return;
            }

            setLoadingUpcomingAppointments(true);
            try {
                const response = await listAppointments({
                    userRoleUuid: user.selectedRole,
                    caregiverUuid: roleUuid,
                    appointmentTypes: ['CALL', 'SCHEDULE'],
                    appointmentStatuses: ['SCHEDULED', 'ONGOING'],
                    queryTs: dayjs().startOf('day').toISOString(),
                    order: 'ASC',
                    paginationLimit: 4
                });

                const list = Array.isArray(response?.data) ? response.data : [];
                setUpcomingAppointments(list);
            } catch {
                setUpcomingAppointments([]);
            } finally {
                setLoadingUpcomingAppointments(false);
            }
        };

        void loadUpcomingAppointments();
    }, [appointmentReloadKey, roleUuid, user.selectedRole]);

    useEffect(() => {
        setAppointmentsPageIndex(0);
        setAppointmentsPageTokens({ 0: null });
        setAppointmentsTotalCount(0);
        setHasNextAppointmentsPage(false);
    }, [roleUuid, user.selectedRole]);

    const canViewNote = useCallback(
        (note) => !note?.priviledged || note?.author?.uuid === user?.selectedRole,
        [user?.selectedRole]
    );

    const currentAppointmentsPageToken = appointmentsPageTokens[appointmentsPageIndex] || null;

    useEffect(() => {
        const loadAppointments = async () => {
            if (!user.selectedRole || !roleUuid) {
                return;
            }

            setLoadingAppointments(true);
            try {
                const response = await listClinicalNotes({
                    userRoleUuid: user.selectedRole,
                    subjectRoleUuid: roleUuid,
                    order: 'DESC',
                    paginationToken: currentAppointmentsPageToken,
                    paginationLimit: 4
                });
                const rawList = Array.isArray(response?.data) ? response.data : [];
                const totalCount = Number(response?.headers?.['pagination-count'] || 0);
                const list = rawList.filter(canViewNote);
                setAppointments(list);
                setAppointmentsTotalCount(totalCount);

                const nextToken = response?.headers?.['pagination-token'] || null;
                const nextPageStart = (appointmentsPageIndex + 1) * 4;
                setHasNextAppointmentsPage(Boolean(nextToken) && nextPageStart < totalCount);
                if (nextToken) {
                    setAppointmentsPageTokens((prev) => ({
                        ...prev,
                        [appointmentsPageIndex + 1]: nextToken
                    }));
                }
            } catch (err) {
                setAppointments([]);
                setAppointmentsTotalCount(0);
                setHasNextAppointmentsPage(false);
            } finally {
                setLoadingAppointments(false);
            }
        };

        void loadAppointments();
    }, [appointmentsPageIndex, canViewNote, currentAppointmentsPageToken, notesReloadKey, roleUuid, user.selectedRole]);

    useEffect(() => {
        const loadMeasurements = async () => {
            if (!user.selectedRole || !roleUuid) {
                return;
            }

            setLoadingMeasurements(true);
            try {
                const measurementTypes = [
                    'BLOOD_PRESSURE',
                    'BLOOD_SUGAR',
                    'BODY_TEMPERATURE',
                    'HEARTRATE',
                    'BLOOD_OXYGEN',
                    'WEIGHT',
                    'EXERCISE'
                ];

                const responses = await Promise.all(
                    measurementTypes.map((measurementType) =>
                        listMeasurements({
                            userRoleUuid: user.selectedRole,
                            subjectRoleUuid: measurementSubjectUuid,
                            measurementTypes: [measurementType],
                            order: 'DESC',
                            paginationLimit: 1
                        })
                    )
                );

                const measurementMap = Object.fromEntries(
                    measurementTypes.map((measurementType, index) => [
                        measurementType,
                        Array.isArray(responses[index]?.data) ? responses[index].data[0] || null : null
                    ])
                );

                const latestTs = measurementTypes
                    .map((measurementType) => measurementMap[measurementType]?.ts || null)
                    .filter(Boolean)
                    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

                const withUnit = (value, unit) =>
                    value != null && value !== '' ? `${value} ${unit}` : '--';

                setLatestMeasurementSummary({
                    date: latestTs ? toPtDateTime(latestTs) : '--',
                    bpSystolic: withUnit(
                        measurementMap.BLOOD_PRESSURE?.data?.systolic,
                        'mmHg'
                    ),
                    bpDiastolic: withUnit(
                        measurementMap.BLOOD_PRESSURE?.data?.diastolic,
                        'mmHg'
                    ),
                    bpDate: measurementMap.BLOOD_PRESSURE?.ts ? toPtDate(measurementMap.BLOOD_PRESSURE.ts) : '--',
                    glucose: withUnit(
                        measurementMap.BLOOD_SUGAR?.data?.glucose,
                        'mg/dL'
                    ),
                    glucoseDate: measurementMap.BLOOD_SUGAR?.ts ? toPtDate(measurementMap.BLOOD_SUGAR.ts) : '--',
                    temperature: withUnit(
                        measurementMap.BODY_TEMPERATURE?.data?.temperature,
                        'ºC'
                    ),
                    temperatureDate: measurementMap.BODY_TEMPERATURE?.ts ? toPtDate(measurementMap.BODY_TEMPERATURE.ts) : '--',
                    hr: withUnit(
                        measurementMap.HEARTRATE?.data?.hr,
                        'bpm'
                    ),
                    hrDate: measurementMap.HEARTRATE?.ts ? toPtDate(measurementMap.HEARTRATE.ts) : '--',
                    oxygen: withUnit(
                        measurementMap.BLOOD_OXYGEN?.data?.saturation,
                        '%'
                    ),
                    oxygenDate: measurementMap.BLOOD_OXYGEN?.ts ? toPtDate(measurementMap.BLOOD_OXYGEN.ts) : '--',
                    weight: withUnit(
                        measurementMap.WEIGHT?.data?.weight,
                        'kg'
                    ),
                    weightDate: measurementMap.WEIGHT?.ts ? toPtDate(measurementMap.WEIGHT.ts) : '--',
                    exercise:
                        measurementMap.EXERCISE?.data?.activity?.trim() || '--',
                    exerciseDate: measurementMap.EXERCISE?.ts ? toPtDate(measurementMap.EXERCISE.ts) : '--'
                });
            } catch (err) {
                setLatestMeasurementSummary(null);
            } finally {
                setLoadingMeasurements(false);
            }
        };

        void loadMeasurements();
    }, [measurementSubjectUuid, roleUuid, user.selectedRole]);

    useEffect(() => {
        const loadMoodMeasurements = async () => {
            if (!user.selectedRole || !roleUuid) {
                setMoodMeasurements({ caregiver: [], patient: [] });
                return;
            }

            try {
                const caregiverRequest = listMeasurements({
                    userRoleUuid: user.selectedRole,
                    subjectRoleUuid: roleUuid,
                    measurementTypes: ['MOOD'],
                    order: 'DESC',
                    paginationLimit: 30
                });

                const patientRequest = measurementSubjectUuid
                    ? listMeasurements({
                        userRoleUuid: user.selectedRole,
                        subjectRoleUuid: measurementSubjectUuid,
                        measurementTypes: ['MOOD'],
                        order: 'DESC',
                        paginationLimit: 30
                    })
                    : Promise.resolve({ data: [] });

                const [caregiverResponse, patientResponse] = await Promise.all([
                    caregiverRequest,
                    patientRequest
                ]);

                setMoodMeasurements({
                    caregiver: Array.isArray(caregiverResponse?.data) ? caregiverResponse.data : [],
                    patient: Array.isArray(patientResponse?.data) ? patientResponse.data : []
                });
            } catch {
                setMoodMeasurements({ caregiver: [], patient: [] });
            }
        };

        void loadMoodMeasurements();
    }, [measurementSubjectUuid, roleUuid, user.selectedRole]);

    useEffect(() => {
        const loadPsychologicalStress = async () => {
            if (!user.selectedRole || !roleUuid) {
                setPsychologicalStressValue('');
                setPsychologicalStressTs('');
                return;
            }

            setLoadingPsychologicalStress(true);
            try {
                const response = await listMeasurements({
                    userRoleUuid: user.selectedRole,
                    subjectRoleUuid: roleUuid,
                    measurementTypes: ['PSYCHOLOGICAL_STRESS'],
                    order: 'DESC',
                    paginationLimit: 1
                });

                const latestItem = Array.isArray(response?.data) ? response.data[0] || null : null;
                setPsychologicalStressValue(latestItem?.data?.value || '');
                setPsychologicalStressTs(latestItem?.ts || '');
            } catch {
                setPsychologicalStressValue('');
                setPsychologicalStressTs('');
            } finally {
                setLoadingPsychologicalStress(false);
            }
        };

        void loadPsychologicalStress();
    }, [roleUuid, user.selectedRole]);

    useEffect(() => {
        const loadWearableRecords = async () => {
            if (!user.selectedRole || activeTab !== 'wearable') {
                return;
            }

            if (!patient?.uuid) {
                setWearableRecords([]);
                setLoadingWearableRecords(false);
                return;
            }

            setLoadingWearableRecords(true);
            try {
                const response = await listPatientRecords({
                    userRoleUuid: user.selectedRole,
                    subjectRoleUuid: patient.uuid,
                    order: 'DESC',
                    paginationLimit: 50
                });

                const list = Array.isArray(response?.data) ? response.data : [];
                setWearableRecords(list);
            } catch {
                setWearableRecords([]);
            } finally {
                setLoadingWearableRecords(false);
            }
        };

        void loadWearableRecords();
    }, [activeTab, patient?.uuid, user.selectedRole, wearableDevice?.uuid]);

    const fallbackName = location.state?.user?.displayName || roleUuid;
    const appointmentsTotalPages = useMemo(() => {
        if (appointmentsTotalCount <= 0) {
            return 1;
        }

        return Math.max(1, Math.ceil(appointmentsTotalCount / 4));
    }, [appointmentsTotalCount]);

    const toPtDateTime = (isoTs) => {
        if (!isoTs) return '--';
        const date = new Date(isoTs);
        if (Number.isNaN(date.getTime())) return isoTs;
        return date.toLocaleString('pt-PT');
    };

    const toPtDate = (isoTs) => {
        if (!isoTs) return '';
        const date = new Date(isoTs);
        if (Number.isNaN(date.getTime())) return isoTs;
        return date.toLocaleDateString('pt-PT', {
            day: '2-digit',
            month: '2-digit'
        });
    };

    const formatClinicianType = (value) => {
        if (!value || value === 'UNKNOWN') {
            return 'Consulta';
        }

        const map = {
            NURSING: 'Enfermagem',
            CARDIOLOGY: 'Cardiologia',
            GERIATRICS: 'Geriatria',
            NUTRITION: 'Nutrição',
            NEUROLOGY: 'Neurologia',
            PSYCHIATRY: 'Psiquiatria',
            PSYCHOLOGY: 'Psicologia',
            PHYSICAL_THERAPY: 'Fisioterapia',
            OCCUPATIONAL_THERAPY: 'Terapia Ocupacional',
            SOCIAL_ASSISTANT: 'Assistente Social',
            GENERAL_PRACTICE: 'Medicina Geral'
        };

        return map[value] || value;
    };

    const isAppointmentActiveNow = (item) => {
        const start = dayjs(item?.ts);
        if (!start.isValid()) {
            return false;
        }

        const end = start.add(Number(item?.slot_duration || 0), 'minute');
        const now = dayjs();
        return (
            (now.isAfter(start) || now.isSame(start)) &&
            (now.isBefore(end) || now.isSame(end))
        );
    };

    const vm = useMemo(() => {
        const extra = caregiver?.user_data || {};
        return {
            name: caregiver?.name || caregiver?.displayName || fallbackName,
            activeSince: toPtDate(caregiver?.created) || '',
            guardianName: extra.guardian_name || '',
            relationship: extra.relationship || '',
            disability: extra.disability || '',
            overloadLevel: extra.overload_level || ''
        };
    }, [caregiver, fallbackName]);

    const patientName = useMemo(() => {
        if (patient?.name) {
            return patient.name;
        }
        return vm.guardianName || '';
    }, [patient?.name, vm.guardianName]);

    const body1Tick = useMemo(
        () => ({
            fontFamily: theme.typography.body1.fontFamily,
            fontSize: 16,
            fontWeight: theme.typography.body1.fontWeight,
            letterSpacing: theme.typography.body1.letterSpacing,
            fill: '#666'
        }),
        [theme]
    );
    const caregiverIsOnline = Boolean(caregiverCallableUser?.isOnline);
    const canDeleteCaregiver = Boolean(roleUuid) && roleUuid !== user?.selectedRole;
    const canDeletePatient = Boolean(patient?.uuid);
    const upcomingAppointmentRows = useMemo(
        () =>
            (upcomingAppointments || []).map((item) => {
                const ts = dayjs(item?.ts);
                return {
                    ...item,
                    dayLabel: ts.isValid() ? ts.locale('pt').format('D MMMM').toUpperCase() : '--',
                    weekdayLabel: ts.isValid()
                        ? ts.locale('pt').format('dddd')
                        : '--',
                    timeLabel: ts.isValid()
                        ? `${ts.format('HH:mm')} - ${ts
                              .add(Number(item?.slot_duration || 0), 'minute')
                              .format('HH:mm')}`
                        : '--',
                    appointmentTypeLabel: formatClinicianType(item?.clinician?.clinician_type),
                    clinicianLabel: item?.clinician?.name || '--',
                    canCall: caregiverIsOnline && isAppointmentActiveNow(item)
                };
            }),
        [caregiverIsOnline, upcomingAppointments]
    );

    const onSavePsychologicalStress = async (nextValue) => {
        if (!user.selectedRole || !roleUuid) {
            return;
        }

        setSavingPsychologicalStress(true);
        try {
            await createMeasurement({
                userRoleUuid: user.selectedRole,
                subjectRoleUuid: roleUuid,
                dtype: 'PSYCHOLOGICAL_STRESS',
                data: {
                    value: nextValue || ''
                }
            });
            setPsychologicalStressValue(nextValue || '');
            setPsychologicalStressTs(new Date().toISOString());
        } catch (err) {
            setError(
                err?.response?.status === 405
                    ? 'Não existe suporte para medicoes de stress psicologico.'
                    : err?.response?.data?.description ||
                      'Nao foi possivel guardar o stress psicologico.'
            );
        } finally {
            setSavingPsychologicalStress(false);
        }
    };

    const getDefaultPatientForm = () => ({
        uuid: '',
        name: '',
        gender: '',
        age: '',
        birthDate: '',
        street: '',
        zip: '',
        parish: '',
        municipality: '',
        idCard: '',
        nif: '',
        healthNumber: '',
        niss: '',
        maritalStatus: '',
        education: '',
        professionalStatus: '',
        disability: ''
    });

    const resetPatientState = () => {
        const emptyPatientForm = getDefaultPatientForm();
        setPatient(null);
        setPatientForm(emptyPatientForm);
        setPatientSnapshot(emptyPatientForm);
        setPatientReadOnly(true);
        setShowPatientForm(false);
    };

    const setTab = (tab) => {
        navigate(
            {
                pathname: location.pathname,
                search: `?tab=${tab}`
            },
            {
                state: location.state
            }
        );
    };

    const onOpenCreateAppointmentDialog = () => {
        setAppointmentDialogError('');
        setAppointmentForm({
            clinician: teamMembers[0]?.userId || user.selectedRole || '',
            date: dayjs().startOf('day'),
            time: '10:00',
            duration: 45
        });
        setAppointmentDialogOpen(true);
    };

    const onCloseCreateAppointmentDialog = () => {
        if (appointmentDialogSubmitting) {
            return;
        }
        setAppointmentDialogOpen(false);
    };

    const onSaveAppointment = async () => {
        if (!user.selectedRole || !roleUuid || !appointmentForm.clinician) {
            setAppointmentDialogError('Dados insuficientes para criar agendamento.');
            return;
        }

        const appointmentMoment = dayjs(
            `${appointmentForm.date.format('YYYY-MM-DD')}T${appointmentForm.time}`
        );

        if (!appointmentMoment.isValid()) {
            setAppointmentDialogError('Define uma data e hora válidas.');
            return;
        }

        setAppointmentDialogSubmitting(true);
        setAppointmentDialogError('');
        try {
            await createAppointmentAdmin({
                userRoleUuid: user.selectedRole,
                payload: {
                    type: 'SCHEDULE',
                    status: 'SCHEDULED',
                    clinician: appointmentForm.clinician,
                    caregiver: roleUuid,
                    ts: appointmentMoment.toISOString(),
                    slot_duration: Number(appointmentForm.duration)
                }
            });
            setAppointmentDialogOpen(false);
            setAppointmentReloadKey((prev) => prev + 1);
        } catch (err) {
            setAppointmentDialogError(
                err?.response?.data?.description || 'Não foi possí­vel criar o agendamento.'
            );
        } finally {
            setAppointmentDialogSubmitting(false);
        }
    };

    const onOpenRescheduleDialog = (appointment) => {
        const ts = dayjs(appointment?.ts);
        setSelectedUpcomingAppointment(appointment);
        setRescheduleError('');
        setRescheduleForm({
            date: ts.isValid() ? ts.startOf('day') : dayjs().startOf('day'),
            time: ts.isValid() ? ts.format('HH:mm') : '10:00',
            duration: Number(appointment?.slot_duration || 45)
        });
        setRescheduleDialogOpen(true);
    };

    const onCloseRescheduleDialog = () => {
        if (rescheduleSubmitting) {
            return;
        }
        setRescheduleDialogOpen(false);
        setSelectedUpcomingAppointment(null);
    };

    const onOpenCancelUpcomingAppointmentDialog = (appointment) => {
        setSelectedUpcomingAppointment(appointment);
        setCancelUpcomingAppointmentError('');
        setCancelUpcomingAppointmentDialogOpen(true);
    };

    const onCloseCancelUpcomingAppointmentDialog = () => {
        if (cancelUpcomingAppointmentSubmitting) {
            return;
        }
        setCancelUpcomingAppointmentDialogOpen(false);
        setCancelUpcomingAppointmentError('');
        setSelectedUpcomingAppointment(null);
    };

    const onConfirmReschedule = async () => {
        if (!user.selectedRole || !selectedUpcomingAppointment?.uuid) {
            return;
        }

        const appointmentMoment = dayjs(
            `${rescheduleForm.date.format('YYYY-MM-DD')}T${rescheduleForm.time}`
        );

        if (!appointmentMoment.isValid()) {
            setRescheduleError('Define uma data e hora válidas.');
            return;
        }

        setRescheduleSubmitting(true);
        setRescheduleError('');
        try {
            await updateAppointmentAdmin({
                userRoleUuid: user.selectedRole,
                payload: {
                    appointment: selectedUpcomingAppointment.uuid,
                    status: 'CANCELLED'
                }
            });

            await createAppointmentAdmin({
                userRoleUuid: user.selectedRole,
                payload: {
                    type: 'SCHEDULE',
                    status: 'SCHEDULED',
                    clinician: selectedUpcomingAppointment?.clinician?.uuid,
                    caregiver: roleUuid,
                    ts: appointmentMoment.toISOString(),
                    slot_duration: Number(rescheduleForm.duration)
                }
            });

            setRescheduleDialogOpen(false);
            setSelectedUpcomingAppointment(null);
            setAppointmentReloadKey((prev) => prev + 1);
        } catch (err) {
            setRescheduleError(
                err?.response?.data?.description || 'Não foi possí­vel reagendar o agendamento.'
            );
        } finally {
            setRescheduleSubmitting(false);
        }
    };

    const onStartUpcomingAppointmentCall = (appointment) => {
        if (!appointment?.canCall || !caregiverCallableUser) {
            return;
        }

        navigate(`/call/${caregiverCallableUser.userId}`, {
            state: { autoDial: true, user: caregiverCallableUser }
        });
    };

    const onConfirmCancelUpcomingAppointment = async () => {
        if (!user.selectedRole || !selectedUpcomingAppointment?.uuid) {
            return;
        }

        setCancelUpcomingAppointmentSubmitting(true);
        setCancelUpcomingAppointmentError('');
        try {
            await updateAppointmentAdmin({
                userRoleUuid: user.selectedRole,
                payload: {
                    appointment: selectedUpcomingAppointment.uuid,
                    status: 'CANCELLED'
                }
            });

            setCancelUpcomingAppointmentDialogOpen(false);
            setSelectedUpcomingAppointment(null);
            setAppointmentReloadKey((prev) => prev + 1);
        } catch (err) {
            setCancelUpcomingAppointmentError(
                err?.response?.data?.description || 'Não foi possí­vel cancelar o agendamento.'
            );
        } finally {
            setCancelUpcomingAppointmentSubmitting(false);
        }
    };

    const onOpenNotes = async (noteUuid) => {
        if (!user.selectedRole || !noteUuid) {
            return;
        }

        setNotesDialogOpen(true);
        setNotesError('');
        setAppointmentNotes([]);
        setLoadingNotes(true);

        try {
            const resp = await fetchClinicalNote({
                userRoleUuid: user.selectedRole,
                noteUuid
            });
            setAppointmentNotes(resp?.data ? [resp.data] : []);
        } catch (err) {
            const description = err?.response?.data?.description || '';
            setNotesError(
                description === 'Method not allowed for external user.'
                    ? 'O conteúdo desta nota foi definido como privado.'
                    : description || 'Não foi possí­vel carregar notas da consulta.'
            );
        } finally {
            setLoadingNotes(false);
        }
    };

    const onCloseNotesDialog = () => {
        setNotesDialogOpen(false);
    };

    const onCreateNote = async (content, options = {}) => {
        if (!user.selectedRole || !roleUuid || !content.trim()) {
            setCreateNoteError('Dados insuficientes para criar nota.');
            return false;
        }

        setCreateNoteError('');
        setCreatingNote(true);
        try {
            const nowIso = new Date().toISOString();
            await createClinicalNote({
                userRoleUuid: user.selectedRole,
                payload: {
                    ts: nowIso,
                    subject: roleUuid,
                    format: 'PLAIN',
                    content: content.trim(),
                    ...(canCreatePrivateNotes ? { priviledged: Boolean(options?.priviledged) } : {})
                }
            });
            setAppointmentsPageIndex(0);
            setAppointmentsPageTokens({ 0: null });
            setHasNextAppointmentsPage(false);
            setNotesReloadKey((prev) => prev + 1);
            return true;
        } catch (err) {
            setCreateNoteError(
                err?.response?.data?.description || 'Não foi possí­vel criar a nota.'
            );
            return false;
        } finally {
            setCreatingNote(false);
        }
    };

    const onFieldChange = (field) => (event) => {
        const value =
            event?.target?.type === 'checkbox' ? event.target.checked : event?.target?.value || '';
        setPersonalForm((prev) => ({
            ...prev,
            [field]: value,
            ...(field === 'birthDate' ? { age: calculateAgeFromDate(value) } : {})
        }));
    };

    const onReferenceContactChange = (index, field) => (event) => {
        const value = event?.target?.value || '';
        setPersonalForm((prev) => ({
            ...prev,
            referenceContacts: prev.referenceContacts.map((contact, contactIndex) =>
                contactIndex === index
                    ? {
                          ...contact,
                          [field]: value
                      }
                    : contact
            )
        }));
    };

    const onAddReferenceContact = () => {
        setPersonalForm((prev) => ({
            ...prev,
            referenceContacts: [...(prev.referenceContacts || []), createEmptyReferenceContact()]
        }));
    };

    const onRemoveReferenceContact = (index) => {
        setPersonalForm((prev) => {
            const nextContacts = (prev.referenceContacts || []).filter(
                (_, contactIndex) => contactIndex !== index
            );

            return {
                ...prev,
                referenceContacts:
                    nextContacts.length > 0 ? nextContacts : [createEmptyReferenceContact()]
            };
        });
    };

    const onEquipmentItemChange = (index, field) => (event) => {
        const value = event?.target?.value || '';
        setPersonalForm((prev) => ({
            ...prev,
            equipmentItems: (prev.equipmentItems || [createEmptyEquipmentItem()]).map((item, itemIndex) =>
                itemIndex === index
                    ? {
                          ...item,
                          [field]: value
                      }
                    : item
            )
        }));
    };

    const onAddEquipmentItem = () => {
        setPersonalForm((prev) => ({
            ...prev,
            equipmentItems: [...(prev.equipmentItems || [createEmptyEquipmentItem()]), createEmptyEquipmentItem()]
        }));
    };

    const onRemoveEquipmentItem = (index) => {
        setPersonalForm((prev) => {
            const nextItems = (prev.equipmentItems || [createEmptyEquipmentItem()]).filter(
                (_, itemIndex) => itemIndex !== index
            );

            return {
                ...prev,
                equipmentItems: nextItems.length > 0 ? nextItems : [createEmptyEquipmentItem()]
            };
        });
    };

    const onEdit = () => {
        setSnapshot(personalForm);
        setReadOnly(false);
    };

    const onCancel = () => {
        if (snapshot) {
            setPersonalForm(snapshot);
        }
        setReadOnly(true);
    };

    const onOpenSubordinatePasswordDialog = () => {
        setSubordinatePasswordForm({
            password: '',
            confirmPassword: ''
        });
        setSubordinatePasswordError('');
        setShowSubordinatePassword(false);
        setShowSubordinatePasswordConfirm(false);
        setSubordinatePasswordDialogOpen(true);
    };

    const onCloseSubordinatePasswordDialog = () => {
        if (subordinatePasswordSubmitting) {
            return;
        }
        setSubordinatePasswordDialogOpen(false);
        setShowSubordinatePassword(false);
        setShowSubordinatePasswordConfirm(false);
    };

    const onSubmitSubordinatePasswordReset = async () => {
        if (!user.selectedRole || !personalForm.roleUuid) {
            setSubordinatePasswordError('Sem role ativa para repor a password.');
            return;
        }

        if (!validatePasswordPolicy(subordinatePasswordForm.password)) {
            setSubordinatePasswordError(passwordPolicyMessage);
            return;
        }

        if (subordinatePasswordForm.password !== subordinatePasswordForm.confirmPassword) {
            setSubordinatePasswordError('As passwords devem coincidir.');
            return;
        }

        setSubordinatePasswordSubmitting(true);
        setSubordinatePasswordError('');

        try {
            await resetSubordinateLoginPassword({
                userRoleUuid: user.selectedRole,
                roleUuid: personalForm.roleUuid,
                password: subordinatePasswordForm.password
            });
            setSubordinatePasswordDialogOpen(false);
        } catch (err) {
            setSubordinatePasswordError(
                err?.response?.data?.description || 'Não foi possí­vel repor a password.'
            );
        } finally {
            setSubordinatePasswordSubmitting(false);
        }
    };

    const onSave = async () => {
        if (!user.selectedRole || !personalForm.roleUuid) {
            setError('Sem role ativa para editar utilizador.');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            const apiGender = toApiGender(personalForm.gender);
            const referenceContacts = (personalForm.referenceContacts || [])
                .map((contact) => ({
                    name: contact.name.trim(),
                    kinship: contact.kinship.trim(),
                    age: contact.age.trim(),
                    phone: contact.phone.trim()
                }))
                .filter((contact) => contact.name || contact.kinship || contact.age || contact.phone);

            const equipmentItems = (personalForm.equipmentItems || [])
                .map((item) => ({
                    type: String(item?.type || '').trim(),
                    details: String(item?.details || '').trim()
                }))
                .filter((item) => item.type || item.details);

            const firstTabletItem = equipmentItems.find(
                (item) => String(item.type || '').trim().toLowerCase() === 'tablet'
            );

            await updateUserByRole({
                userRoleUuid: user.selectedRole,
                payload: {
                    role_uuid: personalForm.roleUuid,
                    name: personalForm.name.trim() || undefined,
                    gender: apiGender,
                    phone: personalForm.phone.trim() || undefined,
                    birth_date: toApiDate(personalForm.birthDate),
                    address: {
                        street: personalForm.street.trim() || undefined,
                        zip: personalForm.zip.trim() || undefined
                    },
                    location: personalForm.locationUuid || undefined,
                    user_data: {
                        age: calculateAgeFromDate(personalForm.birthDate) || null,
                        parish: personalForm.parish.trim() || null,
                        municipality: personalForm.municipality.trim() || null,
                        id_card: personalForm.idCard.trim() || null,
                        professional_status: personalForm.professionalStatus.trim() || null,
                        nif: personalForm.nif.trim() || null,
                        health_number: personalForm.healthNumber.trim() || null,
                        niss: personalForm.niss.trim() || null,
                        marital_status: personalForm.maritalStatus.trim() || null,
                        education: personalForm.education.trim() || null,
                        kinship_to_dependent: personalForm.kinshipToDependent.trim() || null,
                        lives_in_isolation: personalForm.livesInIsolation.trim() || null,
                        reference_contacts: referenceContacts,
                        household_member_name: null,
                        household_member_kinship: null,
                        household_member_age: null,
                        household_member_occupation: null,
                        reference_person_name: null,
                        reference_person_kinship: null,
                        reference_person_age: null,
                        reference_person_occupation: null,
                        care_duration: personalForm.careDuration.trim() || null,
                        has_care_support: personalForm.hasCareSupport.trim() || null,
                        support_type: personalForm.supportType.trim() || null,
                        psychosocial_notes: personalForm.psychosocialNotes.trim() || null,
                        equipment_items: equipmentItems,
                        has_tablet_equipment: Boolean(firstTabletItem),
                        tablet_serial_number: firstTabletItem?.details || null,
                        active_since: toApiDate(personalForm.activeSince),
                        alt_contact_name: null,
                        alt_contact_kinship: null,
                        alt_contact_phone: null,
                        alt_contact_email: null,
                        alt_contact_schedule: null
                    }
                }
            });
            setReadOnly(true);
            setSnapshot(personalForm);
        } catch (err) {
            setError(err?.response?.data?.description || 'Nao foi possivel atualizar utilizador.');
        } finally {
            setSubmitting(false);
        }
    };

    const onPatientChange = (field) => (event) => {
        const value = event?.target?.value || '';
        setPatientForm((prev) => ({
            ...prev,
            [field]: value,
            ...(field === 'birthDate' ? { age: calculateAgeFromDate(value) } : {})
        }));
    };

    const onAddPatientClick = () => {
        setPatientForm(getDefaultPatientForm());
        setPatient(null);
        setPatientReadOnly(false);
        setShowPatientForm(true);
    };

    const onPatientEdit = () => {
        setPatientSnapshot(patientForm);
        setPatientReadOnly(false);
    };

    const onPatientCancel = () => {
        if (patient) {
            setPatientForm(patientSnapshot || patientForm);
            setPatientReadOnly(true);
            return;
        }

        setShowPatientForm(false);
    };

    const onPatientSave = async () => {
        if (!user.selectedRole || !roleUuid) {
            setError('Sem role ativa para gerir paciente.');
            return;
        }

        if (!patientForm.name.trim()) {
            setError('O nome da pessoa a cuidados é obrigatório.');
            return;
        }

        setPatientSubmitting(true);
        setError('');
        try {
            let savedUuid = patientForm.uuid;

            const payloadBase = {
                name: patientForm.name.trim(),
                gender: toApiGender(patientForm.gender),
                birth_date: toApiDate(patientForm.birthDate),
                address: {
                    street: patientForm.street.trim() || undefined,
                    zip: patientForm.zip.trim() || undefined
                },
                user_data: {
                    age: calculateAgeFromDate(patientForm.birthDate) || null,
                    street: patientForm.street.trim() || null,
                    zip: patientForm.zip.trim() || null,
                    parish: patientForm.parish.trim() || null,
                    municipality: patientForm.municipality.trim() || null,
                    id_card: patientForm.idCard.trim() || null,
                    nif: patientForm.nif.trim() || null,
                    health_number: patientForm.healthNumber.trim() || null,
                    niss: patientForm.niss.trim() || null,
                    marital_status: patientForm.maritalStatus.trim() || null,
                    education: patientForm.education.trim() || null,
                    professional_status: patientForm.professionalStatus.trim() || null,
                    disability: patientForm.disability.trim() || null
                }
            };

            if (patient) {
                await updatePatientAdmin({
                    userRoleUuid: user.selectedRole,
                    payload: {
                        patient: patientForm.uuid,
                        ...payloadBase
                    }
                });
                setPatient((prev) => ({ ...(prev || {}), uuid: patientForm.uuid, name: patientForm.name }));
            } else {
                const createResp = await createPatientAdmin({
                    userRoleUuid: user.selectedRole,
                    payload: {
                        ...payloadBase,
                        caregiver: roleUuid
                    }
                });
                const created = createResp?.data || {};
                const createdUuid = created.uuid || '';
                savedUuid = createdUuid || savedUuid;
                setPatientForm((prev) => ({ ...prev, uuid: createdUuid || prev.uuid }));
                setPatient({ uuid: createdUuid, name: created.name || patientForm.name });
            }

            const nextSnapshot = {
                ...patientForm,
                uuid: savedUuid || ''
            };
            setPatientSnapshot(nextSnapshot);
            setPatientReadOnly(true);
            setShowPatientForm(true);
        } catch (err) {
            setError(err?.response?.data?.description || 'Não foi possí­vel guardar paciente.');
        } finally {
            setPatientSubmitting(false);
        }
    };

    const onOpenDeleteCaregiverDialog = () => {
        setDeleteTarget({
            type: 'caregiver',
            caregiverRoleUuid: roleUuid,
            caregiverName: vm.name,
            patientUuid: patient?.uuid || '',
            patientName: patient?.name || patientForm.name || ''
        });
        setDeleteDialogOpen(true);
    };

    const onOpenDeletePatientDialog = () => {
        if (!patient?.uuid) {
            return;
        }

        setDeleteTarget({
            type: 'patient',
            patientUuid: patient.uuid,
            patientName: patient.name || patientForm.name || ''
        });
        setDeleteDialogOpen(true);
    };

    const onCloseDeleteDialog = () => {
        if (deleteSubmitting) {
            return;
        }
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
    };

    const onConfirmDelete = async () => {
        if (!user.selectedRole || !deleteTarget) {
            return;
        }

        setDeleteSubmitting(true);
        setError('');

        let patientDeleted = false;

        try {
            if (deleteTarget.type === 'patient') {
                await deletePatientAdmin({
                    userRoleUuid: user.selectedRole,
                    patientUuid: deleteTarget.patientUuid
                });
                resetPatientState();
                setDeleteDialogOpen(false);
                setDeleteTarget(null);
                return;
            }

            if (deleteTarget.patientUuid) {
                await deletePatientAdmin({
                    userRoleUuid: user.selectedRole,
                    patientUuid: deleteTarget.patientUuid
                });
                patientDeleted = true;
                resetPatientState();
            }

            await deleteUserByRole({
                userRoleUuid: user.selectedRole,
                roleUuid: deleteTarget.caregiverRoleUuid
            });

            navigate('/caregivers');
        } catch (err) {
            if (deleteTarget.type === 'caregiver' && patientDeleted) {
                setError(
                    'O paciente associado foi eliminado, mas não foi possí­vel eliminar o cuidador.'
                );
            } else {
                setError(
                    err?.response?.data?.description || 'Não foi possí­vel eliminar o registo selecionado.'
                );
            }
        } finally {
            setDeleteSubmitting(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt">
        <Box
            sx={{
                px: { xs: 1.5, md: 2 },
                pb: { xs: 1.5, md: 2 },
                pt: 0,
                bgcolor: '#FFFFFF'
            }}
        >
            <Box sx={{ mt: 0 }}>
                <DashboardTabs
                    tabs={dashboardTabs}
                    onTabClick={handleMainTabClick}
                    activeTab="Cuidadores e Pacientes"
                />
            </Box>

            <Paper
                variant="elevation=0"
                sx={{
                    p: { xs: 1.5, md: 2.5 },
                    pb: { xs: 3, md: 4 },
                    borderRadius: '20px',
                    bgcolor: '#FFFFFF',
                    boxShadow: 'none'
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Tooltip title="Voltar à página anterior">
                        <IconButton
                            sx={{
                                width: 40,
                                height: 40,
                                border: '1.5px solid #BDBDBD',
                                bgcolor: '#FFFFFF',
                                color: '#3D3D3D',
                                borderRadius: '12px',
                                '&:hover': {
                                    bgcolor: '#F7F7F7'
                                }
                            }}
                            onClick={() => navigate(-1)}
                        >
                            <ArrowBackIosNewRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Typography variant="h5" fontWeight={700} color="#171717">
                            Cuidadores e Pacientes {'>'}
                        </Typography>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: caregiverIsOnline ? '#26a65b' : '#d64541',
                                flexShrink: 0
                            }}
                        />
                        <Typography variant="h5" fontWeight={700} color="#171717">
                            {vm.name}
                        </Typography>
                    </Stack>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title={caregiverIsOnline ? 'Iniciar videochamada' : 'Iníciar Videochamada'}>
                        <IconButton
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: caregiverIsOnline ? '#E91C24' : '#FFFFFF',
                                color: '#fff',
                                border: caregiverIsOnline ? 'none' : '1.5px solid #D9D9D9',
                                '&:hover': {
                                    bgcolor: caregiverIsOnline ? '#CF1820' : '#F7F7F7'
                                }
                            }}
                            onClick={() =>
                                navigate(`/call/${roleUuid}`, {
                                    state: {
                                        autoDial: true,
                                        wakeIfOffline: true,
                                        user:
                                            caregiverCallableUser ||
                                            caregiver || {
                                                userId: roleUuid,
                                                displayName: vm.name,
                                                name: vm.name,
                                                role: 'PATIENT_CARE_GIVER'
                                            }
                                    }
                                })
                            }
                        >
                            <Box
                                component="img"
                                src={caregiverIsOnline ? '/assets/logos-svg/call.svg' : '/assets/logos-svg/callBlack.svg'}
                                alt="Videochamada"
                                sx={{ width: 20, height: 20, display: 'block' }}
                            />
                        </IconButton>
                    </Tooltip>
                </Stack>

                <Stack direction="row" spacing={1.1} mb={3} flexWrap="wrap" useFlexGap>
                    <Tooltip title="Ver resumo geral do cuidador">
                        <Button
                            variant="outlined"
                            sx={activeTab === 'geral' ? quickTabActiveSx : quickTabSx}
                            onClick={() => setTab('geral')}
                        >
                            Visão Geral
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ver e editar dados pessoais">
                        <Button
                            variant="outlined"
                            sx={activeTab === 'personal' ? quickTabActiveSx : quickTabSx}
                            onClick={() => setTab('personal')}
                        >
                            Dados Pessoais
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ver dados do wearable">
                        <Button
                            variant="outlined"
                            sx={activeTab === 'wearable' ? quickTabActiveSx : quickTabSx}
                            onClick={() => setTab('wearable')}
                        >
                            Wearable
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ver registos de monitorização">
                        <Button
                            variant="outlined"
                            sx={activeTab === 'monitorizacao' ? quickTabActiveSx : quickTabSx}
                            onClick={() => setTab('monitorizacao')}
                        >
                            Monitorização
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ver notas e avaliações">
                        <Button
                            variant="outlined"
                            sx={activeTab === 'avaliacoes' ? quickTabActiveSx : quickTabSx}
                            onClick={() => setTab('avaliacoes')}
                        >
                            Avaliações
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ver documentos internos">
                        <Button
                            variant="outlined"
                            sx={activeTab === 'docs-internal' ? quickTabActiveSx : quickTabSx}
                            onClick={() => setTab('docs-internal')}
                        >
                            Documentos Internos
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ver documentos atribuídos">
                        <Button
                            variant="outlined"
                            sx={activeTab === 'docs-assigned' ? quickTabActiveSx : quickTabSx}
                            onClick={() => setTab('docs-assigned')}
                        >
                            Documentos Atribuí­dos
                        </Button>
                    </Tooltip>
                    <Tooltip title="Ver documentos pessoais">
                        <Button
                            variant="outlined"
                            sx={activeTab === 'docs-personal' ? quickTabActiveSx : quickTabSx}
                            onClick={() => setTab('docs-personal')}
                        >
                            Documentos Pessoais
                        </Button>
                    </Tooltip>
                </Stack>

                {error ? (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                ) : null}

                {activeTab === 'personal' ? (
                    <CaregiverPersonalDataSection
                        form={personalForm}
                        readOnly={readOnly}
                        loading={loading}
                        submitting={submitting}
                        onChange={onFieldChange}
                        onEdit={onEdit}
                        onSave={onSave}
                        onCancel={onCancel}
                        hasPatient={Boolean(patient)}
                        patientForm={patientForm}
                        patientReadOnly={patientReadOnly}
                        patientSubmitting={patientSubmitting}
                        showPatientForm={showPatientForm}
                        onAddPatientClick={onAddPatientClick}
                        onPatientChange={onPatientChange}
                        onPatientEdit={onPatientEdit}
                        onPatientSave={onPatientSave}
                        onPatientCancel={onPatientCancel}
                        onReferenceContactChange={onReferenceContactChange}
                        onAddReferenceContact={onAddReferenceContact}
                        onRemoveReferenceContact={onRemoveReferenceContact}
                        onEquipmentItemChange={onEquipmentItemChange}
                        onAddEquipmentItem={onAddEquipmentItem}
                        onRemoveEquipmentItem={onRemoveEquipmentItem}
                        onDeleteCaregiver={onOpenDeleteCaregiverDialog}
                        onDeletePatient={onOpenDeletePatientDialog}
                        canDeleteCaregiver={canDeleteCaregiver}
                        canDeletePatient={canDeletePatient}
                        deletingCaregiver={deleteSubmitting && deleteTarget?.type === 'caregiver'}
                        deletingPatient={deleteSubmitting && deleteTarget?.type === 'patient'}
                        isSubordinate={personalForm.isSubordinate}
                        subordinateUsername={personalForm.subordinateUsername}
                        onResetSubordinatePassword={onOpenSubordinatePasswordDialog}
                        locationOptions={locationOptions}
                    />
                ) : activeTab === 'avaliacoes' ? (
                    <CaregiverAssessmentsSection
                        caregiverName={vm.name}
                        patientName={patientName}
                        body1Tick={body1Tick}
                        appointments={appointments}
                        loadingAppointments={loadingAppointments}
                        appointmentsPageIndex={appointmentsPageIndex}
                        appointmentsTotalPages={appointmentsTotalPages}
                        hasNextAppointmentsPage={hasNextAppointmentsPage}
                        onPrevAppointmentsPage={() =>
                            setAppointmentsPageIndex((prev) => Math.max(prev - 1, 0))
                        }
                        onNextAppointmentsPage={() => {
                            if (hasNextAppointmentsPage) {
                                setAppointmentsPageIndex((prev) => prev + 1);
                            }
                        }}
                        onOpenNotes={onOpenNotes}
                        onCreateNote={onCreateNote}
                        creatingNote={creatingNote}
                        createNoteError={createNoteError}
                        canCreatePrivateNotes={canCreatePrivateNotes}
                        caregiverMoodMeasurements={moodMeasurements.caregiver}
                        patientMoodMeasurements={moodMeasurements.patient}
                    />
                ) : activeTab === 'wearable' ? (
                    <CaregiverWearableSection
                        patientName={patientName}
                        wearableDevice={wearableDevice}
                        loadingWearableDevice={loadingWearableDevice}
                        wearableRecords={wearableRecords}
                        loadingWearableRecords={loadingWearableRecords}
                    />
                ) : activeTab === 'monitorizacao' ? (
                    <CaregiverMonitoringSection
                        caregiverName={vm.name}
                        patientName={patientName}
                        userRoleUuid={user.selectedRole}
                        subjectRoleUuid={measurementSubjectUuid}
                        initialMeasurementType={initialMonitoringMetric}
                    />
                ) : activeTab === 'docs-internal' ? (
                    <CaregiverDocumentsSection
                        title={`Documentos Internos - ${vm.name}`}
                        caregiverRoleUuid={roleUuid}
                        initialScope="internal"
                        showScopeTabs={false}
                    />
                ) : activeTab === 'docs-assigned' ? (
                    <CaregiverDocumentsSection
                        title={`Documentos Atribuí­dos - ${vm.name}`}
                        caregiverRoleUuid={roleUuid}
                        initialScope="assigned"
                        showScopeTabs={false}
                    />
                ) : activeTab === 'docs-personal' ? (
                    <CaregiverDocumentsSection
                        title={`Documentos Pessoais - ${vm.name}`}
                        caregiverRoleUuid={roleUuid}
                        initialScope="personal"
                        showScopeTabs={false}
                    />
                ) : (
                    <CaregiverGeneralSection
                        vm={vm}
                        caregiver={caregiver}
                        patientName={patientName}
                        notes={appointments}
                        loadingNotes={loadingAppointments}
                        onOpenNotes={onOpenNotes}
                        latestMeasurementSummary={latestMeasurementSummary}
                        loadingMeasurements={loadingMeasurements}
                        caregiverMoodMeasurements={moodMeasurements.caregiver}
                        patientMoodMeasurements={moodMeasurements.patient}
                        psychologicalStressValue={psychologicalStressValue}
                        psychologicalStressTs={psychologicalStressTs}
                        loadingPsychologicalStress={loadingPsychologicalStress}
                        savingPsychologicalStress={savingPsychologicalStress}
                        onSavePsychologicalStress={onSavePsychologicalStress}
                        upcomingAppointments={upcomingAppointmentRows}
                        loadingUpcomingAppointments={loadingUpcomingAppointments}
                        onCreateAppointmentClick={onOpenCreateAppointmentDialog}
                        onRescheduleAppointment={onOpenRescheduleDialog}
                        onCancelAppointment={onOpenCancelUpcomingAppointmentDialog}
                        onStartAppointmentCall={onStartUpcomingAppointmentCall}
                    />
                )}

                {loading ? (
                    <Typography sx={{ mt: 2 }} color="text.secondary">
                        A carregar dados do cuidador...
                    </Typography>
                ) : null}

                <Dialog
                    open={appointmentDialogOpen}
                    onClose={onCloseCreateAppointmentDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>Novo Agendamento</DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        {appointmentDialogError ? (
                            <Typography color="error">{appointmentDialogError}</Typography>
                        ) : null}

                        <TextField
                            select
                            label="Profissional"
                            value={appointmentForm.clinician}
                            onChange={(event) =>
                                setAppointmentForm((prev) => ({
                                    ...prev,
                                    clinician: event.target.value
                                }))
                            }
                            fullWidth
                            sx={{
                                mt: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '14px'
                                }
                            }}
                        >
                            {teamMembers.map((member) => (
                                <MenuItem key={member.userId} value={member.userId}>
                                    {member.displayName || member.userId}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.6} sx={{ mt: 2 }}>
                            <DatePicker
                                label="Data"
                                format="DD/MM/YYYY"
                                value={appointmentForm.date}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        setAppointmentForm((prev) => ({
                                            ...prev,
                                            date: newValue.startOf('day')
                                        }));
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '14px'
                                            }
                                        }
                                    }
                                }}
                            />

                            <TextField
                                label="Hora"
                                type="time"
                                value={appointmentForm.time}
                                onChange={(event) =>
                                    setAppointmentForm((prev) => ({
                                        ...prev,
                                        time: event.target.value
                                    }))
                                }
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '14px'
                                    }
                                }}
                            />
                        </Stack>

                        <TextField
                            select
                            label="Duração"
                            value={appointmentForm.duration}
                            onChange={(event) =>
                                setAppointmentForm((prev) => ({
                                    ...prev,
                                    duration: Number(event.target.value)
                                }))
                            }
                            fullWidth
                            sx={{
                                mt: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '14px'
                                }
                            }}
                        >
                            {[30, 45, 60].map((minutes) => (
                                <MenuItem key={minutes} value={minutes}>
                                    {minutes} minutos
                                </MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button
                            onClick={onCloseCreateAppointmentDialog}
                            variant="outlined"
                            sx={secondaryDialogButtonSx}
                            disabled={appointmentDialogSubmitting}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={onSaveAppointment}
                            variant="contained"
                            sx={{
                                ...secondaryDialogButtonSx,
                                bgcolor: '#D63731',
                                color: '#fff',
                                borderColor: '#D63731',
                                '&:hover': {
                                    bgcolor: '#C7312C',
                                    borderColor: '#C7312C',
                                    borderWidth: '2px'
                                }
                            }}
                            disabled={appointmentDialogSubmitting}
                        >
                            {appointmentDialogSubmitting ? 'A guardar...' : 'Criar Agendamento'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={rescheduleDialogOpen}
                    onClose={onCloseRescheduleDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>Reagendar marcação</DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        {rescheduleError ? (
                            <Typography color="error">{rescheduleError}</Typography>
                        ) : null}

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.6} sx={{ mt: 1 }}>
                            <DatePicker
                                label="Data"
                                format="DD/MM/YYYY"
                                value={rescheduleForm.date}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        setRescheduleForm((prev) => ({
                                            ...prev,
                                            date: newValue.startOf('day')
                                        }));
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '14px'
                                            }
                                        }
                                    }
                                }}
                            />

                            <TextField
                                label="Hora"
                                type="time"
                                value={rescheduleForm.time}
                                onChange={(event) =>
                                    setRescheduleForm((prev) => ({
                                        ...prev,
                                        time: event.target.value
                                    }))
                                }
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '14px'
                                    }
                                }}
                            />
                        </Stack>

                        <TextField
                            select
                            label="Duração"
                            value={rescheduleForm.duration}
                            onChange={(event) =>
                                setRescheduleForm((prev) => ({
                                    ...prev,
                                    duration: Number(event.target.value)
                                }))
                            }
                            fullWidth
                            sx={{
                                mt: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '14px'
                                }
                            }}
                        >
                            {[30, 45, 60].map((minutes) => (
                                <MenuItem key={minutes} value={minutes}>
                                    {minutes} minutos
                                </MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button
                            onClick={onCloseRescheduleDialog}
                            variant="outlined"
                            sx={secondaryDialogButtonSx}
                            disabled={rescheduleSubmitting}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={onConfirmReschedule}
                            variant="contained"
                            sx={{
                                ...secondaryDialogButtonSx,
                                bgcolor: '#D63731',
                                color: '#fff',
                                borderColor: '#D63731',
                                '&:hover': {
                                    bgcolor: '#C7312C',
                                    borderColor: '#C7312C',
                                    borderWidth: '2px'
                                }
                            }}
                            disabled={rescheduleSubmitting}
                        >
                            {rescheduleSubmitting ? 'A reagendar...' : 'Guardar alterações'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={cancelUpcomingAppointmentDialogOpen}
                    onClose={onCloseCancelUpcomingAppointmentDialog}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>Cancelar agendamento</DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        {cancelUpcomingAppointmentError ? (
                            <Typography color="error" sx={{ mb: 1 }}>
                                {cancelUpcomingAppointmentError}
                            </Typography>
                        ) : null}

                        <Typography color="text.secondary">
                            Tem a certeza que quer cancelar este agendamento?
                        </Typography>

                        {selectedUpcomingAppointment ? (
                            <Typography sx={{ mt: 1.5, fontWeight: 600, color: '#3D3D3D' }}>
                                {selectedUpcomingAppointment.dayLabel} {selectedUpcomingAppointment.monthLabel}{' '}
                                {selectedUpcomingAppointment.timeLabel}
                            </Typography>
                        ) : null}
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button
                            onClick={onCloseCancelUpcomingAppointmentDialog}
                            variant="outlined"
                            sx={secondaryDialogButtonSx}
                            disabled={cancelUpcomingAppointmentSubmitting}
                        >
                            Fechar
                        </Button>
                        <Button
                            onClick={onConfirmCancelUpcomingAppointment}
                            variant="contained"
                            sx={{
                                ...secondaryDialogButtonSx,
                                bgcolor: '#D63731',
                                color: '#fff',
                                borderColor: '#D63731',
                                '&:hover': {
                                    bgcolor: '#C7312C',
                                    borderColor: '#C7312C',
                                    borderWidth: '2px'
                                }
                            }}
                            disabled={cancelUpcomingAppointmentSubmitting}
                        >
                            {cancelUpcomingAppointmentSubmitting ? 'A cancelar...' : 'Cancelar agendamento'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={notesDialogOpen}
                    onClose={onCloseNotesDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>Notas da Consulta</DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        {loadingNotes ? (
                            <Typography>A carregar notas...</Typography>
                        ) : null}

                        {!loadingNotes && notesError ? (
                            <Typography color="error">{notesError}</Typography>
                        ) : null}

                        {!loadingNotes && !notesError && appointmentNotes.length === 0 ? (
                            <Typography color="text.secondary">Sem notas para esta consulta.</Typography>
                        ) : null}

                        {!loadingNotes && !notesError && appointmentNotes.length > 0 ? (
                            <Stack spacing={1.2}>
                                {appointmentNotes.map((note, index) => (
                                    <Paper
                                        key={`${note?.created || 'n'}-${index}`}
                                        sx={{
                                            p: 1.8,
                                            borderRadius: '14px',
                                            bgcolor: '#ECECEC',
                                            boxShadow: 'none'
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                                            {note?.author?.name || 'Autor desconhecido'} - {toPtDateTime(note?.created)}
                                        </Typography>
                                        <Typography variant="body2" mt={0.8} color="#3D3D3D">
                                            {note?.content || ''}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Stack>
                        ) : null}
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button onClick={onCloseNotesDialog} variant="outlined" sx={secondaryDialogButtonSx}>
                            Fechar
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={subordinatePasswordDialogOpen}
                    onClose={onCloseSubordinatePasswordDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>Repor password da conta partilhada</DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        <Typography color="#3D3D3D" sx={{ mb: 2 }}>
                            Defina uma nova password para o username{' '}
                            <strong>{personalForm.subordinateUsername || '-'}</strong>.
                        </Typography>

                        {subordinatePasswordError ? (
                            <Typography color="error" sx={{ mb: 2 }}>
                                {subordinatePasswordError}
                            </Typography>
                        ) : null}

                        <Stack spacing={2}>
                            <TextField
                                label="Nova password"
                                type={showSubordinatePassword ? 'text' : 'password'}
                                fullWidth
                                value={subordinatePasswordForm.password}
                                onChange={(event) =>
                                    setSubordinatePasswordForm((prev) => ({
                                        ...prev,
                                        password: event.target.value
                                    }))
                                }
                                helperText={passwordPolicyMessage}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    setShowSubordinatePassword((prev) => !prev)
                                                }
                                            >
                                                {showSubordinatePassword ? (
                                                    <VisibilityOffOutlinedIcon />
                                                ) : (
                                                    <VisibilityOutlinedIcon />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <TextField
                                label="Confirmar password"
                                type={showSubordinatePasswordConfirm ? 'text' : 'password'}
                                fullWidth
                                value={subordinatePasswordForm.confirmPassword}
                                onChange={(event) =>
                                    setSubordinatePasswordForm((prev) => ({
                                        ...prev,
                                        confirmPassword: event.target.value
                                    }))
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    setShowSubordinatePasswordConfirm((prev) => !prev)
                                                }
                                            >
                                                {showSubordinatePasswordConfirm ? (
                                                    <VisibilityOffOutlinedIcon />
                                                ) : (
                                                    <VisibilityOutlinedIcon />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button
                            onClick={onCloseSubordinatePasswordDialog}
                            variant="outlined"
                            sx={secondaryDialogButtonSx}
                            disabled={subordinatePasswordSubmitting}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={onSubmitSubordinatePasswordReset}
                            variant="contained"
                            sx={{
                                ...secondaryDialogButtonSx,
                                bgcolor: '#D63731',
                                color: '#fff',
                                borderColor: '#D63731',
                                '&:hover': {
                                    bgcolor: '#C7312C',
                                    borderColor: '#C7312C',
                                    borderWidth: '2px'
                                }
                            }}
                            disabled={subordinatePasswordSubmitting}
                        >
                            {subordinatePasswordSubmitting ? 'A repor...' : 'Repor password'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={deleteDialogOpen}
                    onClose={onCloseDeleteDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>
                        Confirmar eliminação
                    </DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        {deleteTarget?.type === 'caregiver' && deleteTarget?.patientUuid ? (
                            <Typography color="#3D3D3D">
                                O cuidador <strong>{deleteTarget.caregiverName || '-'}</strong> tem o
                                paciente <strong>{deleteTarget.patientName || '-'}</strong> associado.
                                Ao eliminar este cuidador, vai eliminar tambem a conta do paciente
                                associado.
                            </Typography>
                        ) : deleteTarget?.type === 'caregiver' ? (
                            <Typography color="#3D3D3D">
                                Tem a certeza que pretendes eliminar o cuidador{' '}
                                <strong>{deleteTarget.caregiverName || '-'}</strong>?
                            </Typography>
                        ) : (
                            <Typography color="#3D3D3D">
                                Tem certeza que pretendes eliminar o paciente{' '}
                                <strong>{deleteTarget?.patientName || '-'}</strong>?
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button
                            onClick={onCloseDeleteDialog}
                            variant="outlined"
                            sx={secondaryDialogButtonSx}
                            disabled={deleteSubmitting}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={onConfirmDelete}
                            variant="contained"
                            sx={{
                                ...secondaryDialogButtonSx,
                                bgcolor: '#D63731',
                                color: '#fff',
                                borderColor: '#D63731',
                                '&:hover': {
                                    bgcolor: '#C7312C',
                                    borderColor: '#C7312C',
                                    borderWidth: '2px'
                                }
                            }}
                            disabled={deleteSubmitting}
                        >
                            {deleteSubmitting ? 'A eliminar...' : 'Eliminar'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
        </LocalizationProvider>
    );
};

export default CaregiverDashboard;









